import React from "react";
import { Button, TextField, Typography, Table, TableBody, TableCell, TableHead, TableRow, ButtonGroup, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { postApi } from '../../utils/apiFunction';
import DatePicker from "../../shared/DatePicker";

import { Box } from "@mui/system";
import { textfieldDateFormat, startOfWeek, shiftWeek } from '../../utils/dateFunctions';
import { renderStatusArrow } from '../../utils/flipStatusUtils';
import { hasWarehouseEditAccess } from "./utils/access";
import { useTheme } from "@mui/material/styles";
import SubmitButtonDisplayer from "../../components/SubmitButtonDisplayer";
import { ACCESS, ACCESS_SECTIONS } from "../../utils/constants/access";
import { numberFormat } from "../../utils/numberFunctions";
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
const panelStyle = {
    position: "absolute",
    top: 80,
    left: 0,
    padding: 16,
    width: 270,
    backgroundColor: "white",
    zIndex: 1000
};

const BulkSelection = ({ groupFeatures, onClose, onRefresh, access,farms }) => {
    const [bulkDueDate, setBulkDueDate] = React.useState("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [completed, setCompleted] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [mode, setMode] = React.useState("dueDate"); // 'dueDate' | 'pickup' | 'flip'
    const [showDetails, setShowDetails] = React.useState(false);
    const [warehouses, setWarehouses] = React.useState([]);
    const [pickupDate, setPickupDate] = React.useState(new Date());
    const [flipDate, setFlipDate] = React.useState(new Date());
    const [flipStatus, setFlipStatus] = React.useState(1); // 1 = "<-", 2 = "-", 3 = "->"
    const theme = useTheme();
    const groupIds = groupFeatures.map(f => f.properties.GroupId);
    const Swal = useSwalWrapper();
    
   
    
    

    const resetState = () => {
        setIsSubmitting(false);
        setError(null);
        setCompleted(false);
    };

    const handleSetFlipStatus = async () => {
        if (!groupIds || groupIds.length === 0) return;
        setError(null);
        setCompleted(false);
        setIsSubmitting(true);
        try {
            await postApi("/Farms/Groups/SetFlipStatus", {
                GroupIds: groupIds,
                Date: textfieldDateFormat(flipDate),
                FlipStatus: flipStatus
            });
            if (onRefresh) onRefresh();
            setCompleted(true);
        } catch (err) {
            console.error(err);
            setError("Failed to set flip status");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClearDueDate = async () => {
        if (!groupIds || groupIds.length === 0) return;
        setError(null);
        setCompleted(false);
        setIsSubmitting(true);
        try {
            await postApi("/Farms/Groups/ClearDueDate", { GroupIds: groupIds });
            if (onRefresh) onRefresh();
            setCompleted(true);
        } catch (err) {
            console.error(err);
            setError("Failed to clear due dates");
        } finally {
            setIsSubmitting(false);
            // keep open so user can see success/error; caller can still close
        }
    };

    const handleSetDueDate = async () => {
        if (!groupIds || groupIds.length === 0) return;
        setError(null);
        setCompleted(false);
        setIsSubmitting(true);
        try {
            const date = textfieldDateFormat(new Date(bulkDueDate));
            await postApi("/Farms/Groups/SetDueDate", { GroupIds: groupIds, Date: date });
            if (onRefresh) onRefresh();
            setCompleted(true);
        } catch (err) {
            console.error(err);
            setError("Failed to set due dates");
        } finally {
            setIsSubmitting(false);
        }
    };

    const validateSetDueDate = () => {
        if (!bulkDueDate) {
            Swal.fire({
                html: 'You must select a due date',
                icon: 'error',
                title: 'Oops...'
            });
            return;
        }
        handleSetDueDate();
    };

    const handlePickupAll = async () => {
        if (!groupIds || groupIds.length === 0) return;
        setError(null);
        setCompleted(false);
        setIsSubmitting(true);
        try {
            await postApi("/Farms/Groups/PickupAll", { GroupIds: groupIds, Date: textfieldDateFormat(pickupDate) });
            if (onRefresh) onRefresh();
            setCompleted(true);
        } catch (err) {
            console.error(err);
            setError("Failed to pick up groups");
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleDetails = async () => {
        // In a real implementation you might load details from the server here.
        // For now we just toggle the visibility; Map-side wiring can fill groupDetails.
        setShowDetails(prev => !prev);
    };
    const warehouseId = groupFeatures?.[0]?.properties?.warehouseId
    return (
         <Box
            component="form"
            sx={{
                '& .MuiTextField-root': { m: 1, width: '24ch' },
                background: theme.palette.background.paper,
                zIndex: '1000',
                position: 'absolute',
                width: showDetails ? '520px' : '270px',
                maxWidth: '85vw',
                padding: '15px'
            }}
            noValidate
            autoComplete="off"
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" gutterBottom>
                    Bulk Selection
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <ButtonGroup size="small" variant="outlined">
                        <Button
                            variant={mode === 'dueDate' ? 'contained' : 'outlined'}
                            onClick={() => setMode('dueDate')}
                        >
                            Due Date
                        </Button>
                        <Button
                            variant={mode === 'pickup' ? 'contained' : 'outlined'}
                            onClick={() => setMode('pickup')}
                        >
                            Pick Up
                        </Button>
                        <Button
                            variant={mode === 'flip' ? 'contained' : 'outlined'}
                            onClick={() => setMode('flip')}
                        >
                            Flip
                        </Button>
                    </ButtonGroup>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
            </div>
            <Typography variant="body2" gutterBottom>
                {groupIds ? groupIds.length : 0} group(s) selected.
            </Typography>
            {mode === 'dueDate' && (
                <>
                    <TextField
                        label="Due Date"
                        type="date"
                        value={bulkDueDate}
                        onChange={e => setBulkDueDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        margin="dense"
                    />
                    <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <SubmitButtonDisplayer
                            title="Clear Due Date"
                            completed={completed}
                            error={error}
                            loading={isSubmitting}
                            allowed={hasWarehouseEditAccess(access, warehouseId,farms)}
                            submitFunc={handleClearDueDate}
                            handleClose={onClose}
                            successMessage="Close"
                        />
                        <SubmitButtonDisplayer
                            title="Set Due Date"
                            completed={completed}
                            error={error}
                            loading={isSubmitting}
                            allowed={hasWarehouseEditAccess(access, warehouseId,farms)}
                            submitFunc={validateSetDueDate}
                            handleClose={onClose}
                            successMessage="Close"
                        />
                    </div>
                </>
            )}
            {mode === 'pickup' && (
                <div
                    style={{
                        marginTop: 16,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 8
                    }}
                >
                    <DatePicker
                        date={pickupDate}
                        setDate={setPickupDate}
                        label="Date"
                        disabled={completed}
                    />

                    <SubmitButtonDisplayer
                        title="Pick Up All"
                        completed={completed}
                        error={error}
                        loading={isSubmitting}
                        allowed={hasWarehouseEditAccess(access, warehouseId,farms)}
                        submitFunc={handlePickupAll}
                        handleClose={onClose}
                        successMessage="Close"
                    />
                </div>
            )}

            {mode === 'flip' && (
                <div
                    style={{
                        marginTop: 16,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 8
                    }}
                >
                    <DatePicker
                        date={flipDate}
                        setDate={setFlipDate}
                        label="Flip Date"
                        disabled={completed}
                    />

                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            variant={flipStatus === 1 ? 'contained' : 'outlined'}
                            onClick={() => setFlipStatus(1)}
                        >
                            {renderStatusArrow(1)}
                        </Button>
                        <Button
                            variant={flipStatus === 2 ? 'contained' : 'outlined'}
                            onClick={() => setFlipStatus(2)}
                        >
                            {renderStatusArrow(2)}
                        </Button>
                        <Button
                            variant={flipStatus === 3 ? 'contained' : 'outlined'}
                            onClick={() => setFlipStatus(3)}
                        >
                            {renderStatusArrow(3)}
                        </Button>
                    </div>

                    <SubmitButtonDisplayer
                        title="Set Flip"
                        completed={completed}
                        error={error}
                        loading={isSubmitting}
                        allowed={hasWarehouseEditAccess(access, warehouseId,farms)}
                        submitFunc={handleSetFlipStatus}
                        handleClose={onClose}
                        successMessage="Close"
                    />
                </div>
            )}

            <div style={{ marginTop: 8 }}>
                <Button size="small" onClick={toggleDetails}>
                    {showDetails ? 'Hide Details' : 'Show Details'}
                </Button>
            </div>

            {showDetails && groupFeatures && groupFeatures.length > 0 && (
                <div style={{ marginTop: 8, maxHeight: 200, overflow: 'auto' }}>
                    {(() => {
                        const totals = groupFeatures.reduce(
                            (acc, f) => {
                                const p = f.properties || {};
                                const qty = Number(p.TotalUnits) || 0;
                                const avg = Number(p.Average) || 0;
                                acc.totalQuantity += qty;
                                acc.totalOysters += avg * qty;
                                return acc;
                            },
                            { totalQuantity: 0, totalOysters: 0 }
                        );

                        const totalDozens = totals.totalOysters / 12;
                        const totalDozensInt = Math.round(totalDozens);

                        return (
                            <Table
                                size="small"
                                sx={{
                                    '& th, & td': {
                                        padding: '2px 4px',
                                        borderBottom: '1px solid rgba(224,224,224,0.6)',
                                        fontSize: '0.75rem'
                                    }
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Batch</TableCell>
                                        <TableCell>Grade</TableCell>
                                        <TableCell>Unit Type</TableCell>
                                        <TableCell>Average</TableCell>
                                        <TableCell>Quantity</TableCell>
                                        <TableCell>Total Oysters</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {groupFeatures.map((f, idx) => {
                                        const p = f.properties || {};
                                        const qty = Number(p.TotalUnits) || 0;
                                        const avg = Number(p.Average) || 0;
                                        const totalOysters = avg * qty;
                                        const dozens = totalOysters / 12;
                                        const dozensInt = Math.round(dozens);

                                        return (
                                            <TableRow key={idx}>
                                                <TableCell>{p.CropLabel}</TableCell>
                                                <TableCell>{p.Grade}</TableCell>
                                                <TableCell>{p.UnitType}</TableCell>
                                                <TableCell>{p.Average}</TableCell>
                                                <TableCell>{numberFormat(p.TotalUnits)}</TableCell>
                                                <TableCell>{`${numberFormat(totalOysters)} (${numberFormat(dozensInt)} doz)`}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    <TableRow>
                                        <TableCell colSpan={4} style={{ fontWeight: 600 }}>Total</TableCell>
                                        <TableCell style={{ fontWeight: 600 }}>{numberFormat(totals.totalQuantity)}</TableCell>
                                        <TableCell style={{ fontWeight: 600 }}>
                                            {`${numberFormat(totals.totalOysters)} (${numberFormat(totalDozensInt)} doz)`}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        );
                    })()}
                </div>
            )}
        </Box>
    );
};

export default BulkSelection;
